import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "aquadias-cookie-consent";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Slight delay so it doesn't flash on first paint
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const handleConsent = (value: "accepted" | "declined") => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, timestamp: new Date().toISOString() })
      );
    } catch {
      // ignore storage errors
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-samtycke"
      className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-border/60 bg-background/95 backdrop-blur-md shadow-2xl p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-shrink-0 hidden sm:flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Cookie className="h-5 w-5" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-sm sm:text-base font-semibold text-foreground">
                Vi värnar om din integritet
              </h2>
              <button
                type="button"
                onClick={() => handleConsent("declined")}
                className="sm:hidden -mt-1 -mr-1 p-1 rounded-md text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Stäng"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              Vi använder cookies för att förbättra din upplevelse, analysera
              trafik och optimera vår tjänst. Läs mer i vår{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-2 text-foreground hover:text-primary transition-colors"
              >
                integritetspolicy
              </Link>
              .
            </p>

            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleConsent("declined")}
                className="text-muted-foreground hover:text-foreground"
              >
                Endast nödvändiga
              </Button>
              <Button
                size="sm"
                onClick={() => handleConsent("accepted")}
                className="font-semibold"
              >
                Acceptera alla
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
